import React, { ReactNode } from 'react';
import { observer } from 'mobx-react';
import { Form, Select } from 'antd';
import { Rule } from 'antd/es/form';
import { filterOption } from './filterOption';
import cx from 'classnames';
import { IGetApiType, ISelect, ISelectList } from '../../interfaces';
import { useUiIsLoading, useUiIsSpinnerFound } from '../../hooks';
import { formatDataTestAttribute } from '../../instruments';
import { Catcher } from '../Catcher';

// ToDo: 21.08.2020 -   Use throughout the project!

interface IFormSelect {
    dataTest?: string;
    defaultValue?: string;
    disabled?: boolean;
    exceptionIds?: Array<string | undefined>;
    fieldName: string | [number, string];
    idSpinners?: IGetApiType[];
    isAddonAfter?: boolean;
    isAddonAfterLong?: boolean;
    isAddonBefore?: boolean;
    isGetPopupContainerEnabled?: boolean;
    isNotClear?: boolean;
    isShortForm?: boolean;
    isShowSearch?: boolean;
    label?: string;
    mode?: 'multiple' | 'tags' | undefined;
    onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
    onChange?: (list: Array<string> | string) => void;
    placeholder?: ReactNode;
    required?: boolean;
    rules?: Array<Rule>;
    useSelectHook: () => ISelectList;
}

export const FormSelect: React.FC<IFormSelect> = observer(
    ({
        dataTest = '',
        defaultValue,
        disabled = false,
        exceptionIds = [],
        fieldName,
        idSpinners,
        isAddonAfter,
        isAddonAfterLong,
        isAddonBefore,
        isGetPopupContainerEnabled = true,
        isNotClear,
        isShowSearch = true,
        label = '',
        mode,
        onBlur,
        onChange,
        placeholder,
        required = false,
        rules = [],
        useSelectHook,
    }: IFormSelect) => {
        const { Option } = Select;
        const isLoading = idSpinners ? useUiIsSpinnerFound(idSpinners) : useUiIsLoading();
        const select = useSelectHook();

        const selectJSX = select
            ?.filter((s) => !exceptionIds || !exceptionIds.includes(s.id))
            ?.map(
                (s: ISelect, index): JSX.Element => (
                    <Option
                        key={s.id}
                        value={s.id}
                        disabled={s.disabled}
                        data-test={formatDataTestAttribute(dataTest, 'FormSelect', `Option${index}`)}
                    >
                        <div style={{ display: 'flex' }}>{s.name}</div>
                    </Option>
                ),
            );

        return (
            <Catcher>
                <Form.Item
                    name={fieldName}
                    label={label}
                    rules={rules}
                    className={cx({
                        ['ant-form-item-addonSelect']: isAddonBefore,
                        ['ant-form-item-addonSelectAfter']: isAddonAfter,
                        ['ant-form-item-addonSelectAfterLong']: isAddonAfterLong,
                    })}
                    data-test={formatDataTestAttribute(dataTest, 'FormSelect', 'FormItem')}
                    required={required}
                >
                    <Select
                        allowClear={!isNotClear}
                        defaultValue={defaultValue}
                        disabled={disabled}
                        filterOption={filterOption}
                        getPopupContainer={
                            isGetPopupContainerEnabled ? (trigger): HTMLElement => trigger.parentElement : undefined
                        }
                        loading={isLoading}
                        mode={mode}
                        onChange={onChange}
                        onBlur={onBlur}
                        placeholder={placeholder || label}
                        showSearch={isShowSearch}
                        data-test={formatDataTestAttribute(dataTest, 'FormSelect', 'Select')}
                        optionLabelProp={'children'}
                    >
                        {selectJSX}
                    </Select>
                </Form.Item>
            </Catcher>
        );
    },
);
